import { Link } from "@/components/LangLink";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLoginModal } from "@/App";
import { CONTACT } from "@/data/site";

export default function Footer() {
  const openLogin = useLoginModal();
  const { t } = useTranslation();

  return (
    <footer data-testid="site-footer" className="bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <img src="/logo-gold.png" alt="Delux Concierge" className="h-28 w-auto mb-8" />
            <p className="text-white/45 font-light text-sm leading-relaxed max-w-xs">
              {t("Five-star luxury lifestyle management and personal concierge services, all over the world. A members-only atelier for the extraordinary.")}
            </p>
          </div>

          <div className="md:col-span-2 md:col-start-6">
            <p className="overline-lux mb-7">{t("Explore")}</p>
            <ul className="space-y-4 text-sm text-white/55 font-light">
              <li><Link to="/services" data-testid="footer-link-our-services" className="hover:text-gold transition-colors duration-300">{t("Our Services")}</Link></li>
              <li><Link to="/membership" data-testid="footer-link-membership" className="hover:text-gold transition-colors duration-300">{t("Membership")}</Link></li>
              <li><Link to="/about" data-testid="footer-link-about-us" className="hover:text-gold transition-colors duration-300">{t("About Us")}</Link></li>
              <li><Link to="/blog" data-testid="footer-link-blog" className="hover:text-gold transition-colors duration-300">{t("Blog")}</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact-us" className="hover:text-gold transition-colors duration-300">{t("Contact Us")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="overline-lux mb-7">{t("Company")}</p>
            <ul className="space-y-4 text-sm text-white/55 font-light">
              <li><Link to="/about" data-testid="footer-link-our-story" className="hover:text-gold transition-colors duration-300">{t("Our Story")}</Link></li>
              <li><Link to="/contact?type=admission" data-testid="footer-link-request-admission" className="hover:text-gold transition-colors duration-300">{t("Request Admission")}</Link></li>
              <li>
                <button onClick={openLogin} data-testid="footer-member-login" className="hover:text-gold transition-colors duration-300">
                  {t("Member Login")}
                </button>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline-lux mb-7">{t("Connect")}</p>
            <ul className="space-y-4 text-sm text-white/55 font-light">
              <li>
                <a href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`} data-testid="footer-phone" className="hover:text-gold transition-colors duration-300">
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="hover:text-gold transition-colors duration-300">
                  {CONTACT.email}
                </a>
              </li>
              <li className="text-white/40">{t("London · Dubai · Monaco")}</li>
            </ul>
            <div className="flex gap-3 mt-8">
              {[
                [Instagram, "instagram", "https://www.instagram.com/delux.concierge/"],
                [Facebook, "facebook", "#"],
                [Linkedin, "linkedin", "#"],
              ].map(([Icon, name, url]) => (
                <a
                  key={name}
                  href={url}
                  target={url !== "#" ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`footer-social-${name}`}
                  aria-label={name}
                  className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/55 hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <Icon strokeWidth={1.25} size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-7 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[0.68rem] text-white/35 tracking-wider">
          <p>{t("© 2026 Delux Concierge LTD. All rights reserved.")}</p>
          <div className="flex flex-wrap gap-x-8 gap-y-2 justify-center">
            <a href="/legal/terms-and-conditions.pdf" target="_blank" rel="noreferrer" data-testid="footer-terms-link" className="hover:text-gold transition-colors duration-300">
              {t("Terms & Conditions")}
            </a>
            <a href="/legal/cookie-policy.docx" data-testid="footer-cookie-link" className="hover:text-gold transition-colors duration-300">
              {t("Cookie Policy")}
            </a>
            <a href="/legal/code-of-conduct.docx" data-testid="footer-conduct-link" className="hover:text-gold transition-colors duration-300">
              {t("Code of Conduct")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
